import type { Profile } from "@shyne/shared/api";
import {SessionUser} from "@/models/user.model.ts";
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import supabase from "@/lib/supabase.ts";
import {AuthRepository} from "@/repositories/auth.repository.ts";
import { toSessionUser } from "@/repositories/profile-records";

interface AuthState {
    user: SessionUser | null;
    signupPending: boolean;
    loading: boolean;
}

const initialState: AuthState = {
    user: null,
    signupPending: false,
    loading: true
};

const SIGNED_OUT = { user: null, signupPending: false };

export const fetchUser = createAsyncThunk('auth/fetchUser', async () => {
    const authRes = await supabase.auth.getUser();
    const authUser = authRes.data.user;
    if (!authUser) return SIGNED_OUT;

    const user = await AuthRepository.getSessionUser(authUser.id);
    if (user) return { user, signupPending: false };
    return { user: null, signupPending: await AuthRepository.signupPending() };
});

export const logoutUser = createAsyncThunk('auth/logout', async () => {
    await supabase.auth.signOut();
    return null;
});

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        profileChanged: (state, action: PayloadAction<Profile>) => {
            if (state.user) state.user = toSessionUser(action.payload, state.user.authId);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUser.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchUser.fulfilled, (state, action) => {
                state.user = action.payload.user;
                state.signupPending = action.payload.signupPending;
                state.loading = false;
            })
            .addCase(fetchUser.rejected, (state) => {
                state.user = null;
                state.signupPending = false;
                state.loading = false;
            })
            .addCase(logoutUser.fulfilled, (state) => {
                state.user = null;
                state.signupPending = false;
                state.loading = false;
            });
    }
});

export const { profileChanged } = authSlice.actions;
export default authSlice.reducer;
